import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedInput  from './SharedInput';
import SharedButton from './SharedButton';

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const TeacherManagementForm = () => {
  const [teachers,          setTeachers]          = useState([]);
  const [products,          setProducts]          = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [availableLevels,   setAvailableLevels]   = useState([]);

  const [teacherName,                  setTeacherName]                  = useState('');
  const [teacherDisciplines,           setTeacherDisciplines]           = useState([]);
  const [teacherSubdisciplineSchedules,setTeacherSubdisciplineSchedules]= useState([]);
  const [teacherSalary,                setTeacherSalary]                = useState('');
  const [editingTeacher,               setEditingTeacher]               = useState(null);

  const [availableDisciplines,   setAvailableDisciplines]   = useState([]);
  const [availableSubdisciplines,setAvailableSubdisciplines]= useState([]);

  const [currentSub,   setCurrentSub]   = useState('');
  const [currentLevel, setCurrentLevel] = useState('');
  const [currentDays,  setCurrentDays]  = useState([]);
  const [currentTime,  setCurrentTime]  = useState('');

  const [salaryStart, setSalaryStart] = useState('');
  const [salaryEnd,   setSalaryEnd]   = useState('');
  const [msg,         setMsg]         = useState(null);

  const daysOfWeek = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  // Carga inicial
  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, teachRes, attRes, lvlRes] = await Promise.all([
          fetch('/api/list_products.php',   { credentials: 'include' }),
          fetch('/api/list_teachers.php',   { credentials: 'include' }),
          fetch('/api/list_attendance.php', { credentials: 'include' }),
          fetch('/api/list_levels.php',     { credentials: 'include' }),
        ]);
        const [prodData, teachData, attData, lvlData] = await Promise.all([
          prodRes.json(), teachRes.json(), attRes.json(), lvlRes.json()
        ]);
        setProducts(       Array.isArray(prodData)  ? prodData  : []);
        setTeachers(       Array.isArray(teachData) ? teachData : []);
        setAttendanceRecords(Array.isArray(attData) ? attData   : []);
        setAvailableLevels(Array.isArray(lvlData)   ? lvlData   : []);
      } catch (err) {
        console.error('TeacherManagement load error:', err);
      }
    };
    load();
  }, []);

  // Derivar disciplinas de productos
  useEffect(() => {
    setAvailableDisciplines(products.filter(p => p.type === 'discipline'));
  }, [products]);

  // Derivar subdisciplinas de las disciplinas seleccionadas
  useEffect(() => {
    const matched = availableDisciplines.filter(d => teacherDisciplines.includes(String(d.id)));
    const subs    = matched.flatMap(d => d.subdisciplines || []);
    setAvailableSubdisciplines(subs);
    setTeacherSubdisciplineSchedules(prev => prev.filter(item => subs.includes(item.subdiscipline)));
  }, [teacherDisciplines, availableDisciplines]);

  const handleDisciplineChange = e => {
    setTeacherDisciplines(Array.from(e.target.selectedOptions).map(o => String(o.value)));
  };

  const handleDaysChange = e => {
    setCurrentDays(Array.from(e.target.selectedOptions).map(o => o.value));
  };

  const handleAddSchedule = () => {
    if (!currentSub || !currentLevel || !currentDays.length || !currentTime) {
      showMsg('error', 'Completa subdisciplina, nivel, días y hora.');
      return;
    }
    const item = { subdiscipline: currentSub, level: currentLevel, days: currentDays, time: currentTime };
    const dup  = teacherSubdisciplineSchedules.some(i =>
      i.subdiscipline === item.subdiscipline && i.level === item.level && i.time === item.time &&
      i.days.length === item.days.length && i.days.every(d => item.days.includes(d))
    );
    if (dup) { showMsg('error', 'Este horario ya está agregado.'); return; }
    setTeacherSubdisciplineSchedules(prev => [...prev, item]);
    setCurrentSub(''); setCurrentLevel(''); setCurrentDays([]); setCurrentTime('');
  };

  const handleRemoveSchedule = rem => {
    setTeacherSubdisciplineSchedules(prev =>
      prev.filter(i => !(
        i.subdiscipline === rem.subdiscipline && i.level === rem.level &&
        i.time === rem.time && i.days.length === rem.days.length &&
        i.days.every(d => rem.days.includes(d))
      ))
    );
  };

  const resetForm = () => {
    setEditingTeacher(null);
    setTeacherName('');
    setTeacherDisciplines([]);
    setTeacherSubdisciplineSchedules([]);
    setTeacherSalary('');
    setCurrentSub(''); setCurrentLevel(''); setCurrentDays([]); setCurrentTime('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!teacherName || !teacherDisciplines.length || !teacherSalary) {
      showMsg('error', 'Completa nombre, disciplinas y salario.');
      return;
    }
    // FIX: user_id viene de la sesión PHP, no de localStorage
    const payload = {
      id:                     editingTeacher?.id || null,
      name:                   teacherName,
      disciplines:            teacherDisciplines,
      subdisciplineSchedules: teacherSubdisciplineSchedules,
      salaryPerClass:         parseFloat(teacherSalary),
    };
    try {
      const res  = await fetch('/api/create_or_update_teacher.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Error');

      const teachRes  = await fetch('/api/list_teachers.php', { credentials: 'include' });
      const teachData = await teachRes.json();
      setTeachers(Array.isArray(teachData) ? teachData : []);
      showMsg('success', editingTeacher ? 'Maestro actualizado.' : 'Maestro registrado.');
      resetForm();
    } catch (err) {
      showMsg('error', err.message);
    }
  };

  const handleEdit = t => {
    setEditingTeacher(t);
    setTeacherName(t.name);
    let ids = (t.disciplines || []).map(String);
    // Si vienen nombres en lugar de IDs, resolverlos
    if (ids.length && isNaN(ids[0])) {
      ids = ids.map(n => {
        const f = availableDisciplines.find(d => d.name === n);
        return f ? String(f.id) : null;
      }).filter(Boolean);
    }
    setTeacherDisciplines(ids);
    setTeacherSubdisciplineSchedules(Array.isArray(t.subdisciplineSchedules) ? t.subdisciplineSchedules : []);
    setTeacherSalary(String(t.salaryPerClass || ''));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este maestro?')) return;
    try {
      const res  = await fetch('/api/delete_teacher.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Error');
      setTeachers(prev => prev.filter(x => x.id !== id));
      showMsg('success', 'Maestro eliminado.');
    } catch (err) {
      showMsg('error', err.message);
    }
  };

  // Calcular salario acumulado por rango de fechas
  const calculateSalary = t => {
    if (!Array.isArray(attendanceRecords)) return 0;
    const start = salaryStart ? new Date(salaryStart + 'T00:00:00').getTime() : 0;
    const end   = salaryEnd   ? new Date(salaryEnd   + 'T23:59:59').getTime() : Infinity;
    let count   = 0;
    attendanceRecords.forEach(rec => {
      const rd = new Date(rec.date + 'T00:00:00').getTime();
      if (
        rd >= start && rd <= end &&
        rec.teacher === t.name &&
        rec.teacherAttendance?.[t.id] === true &&
        Array.isArray(t.subdisciplineSchedules) &&
        t.subdisciplineSchedules.some(s =>
          s.subdiscipline === rec.subdiscipline && s.level === rec.level
        )
      ) count++;
    });
    return count * (t.salaryPerClass || 0);
  };

  const getDisciplineNames = disciplines =>
    (disciplines || []).map(id => {
      const d = availableDisciplines.find(x => String(x.id) === String(id));
      return d ? d.name : id;
    }).join(', ');

  return (
    <SharedCard title="Gestión de Maestros">

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      {/* ── Formulario ── */}
      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 mb-8 shadow-sm">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {editingTeacher ? `✏️ Editando: ${editingTeacher.name}` : '➕ Registrar maestro'}
          </h3>
          {editingTeacher && (
            <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SharedInput
            label="Nombre del maestro*"
            value={teacherName}
            onChange={e => setTeacherName(e.target.value)}
            placeholder="Ej: Maestra Sofía"
          />
          <SharedInput
            label="Salario por clase*"
            type="number"
            value={teacherSalary}
            onChange={e => setTeacherSalary(e.target.value)}
            placeholder="Ej: 250.00"
          />
        </div>

        {/* Disciplinas */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            Disciplinas* <span className="text-gray-400 font-normal">(Ctrl/Cmd para múltiples)</span>
          </label>
          <select
            multiple
            value={teacherDisciplines}
            onChange={handleDisciplineChange}
            className="w-full h-28 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
          >
            {availableDisciplines.map(d => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>
        </div>

        {/* Panel de horario */}
        {teacherDisciplines.length > 0 && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <h4 className="text-sm font-bold text-gray-700 mb-3">Agregar horario</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Subdisciplina</label>
                <select
                  value={currentSub}
                  onChange={e => setCurrentSub(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                >
                  <option value="">Selecciona</option>
                  {availableSubdisciplines.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nivel</label>
                <select
                  value={currentLevel}
                  onChange={e => setCurrentLevel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                >
                  <option value="">Selecciona</option>
                  {availableLevels.map(l => (
                    <option key={l.id ?? l} value={l.name ?? l}>{l.name ?? l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Días <span className="text-gray-400 font-normal">(Ctrl/Cmd para múltiples)</span>
                </label>
                <select
                  multiple
                  value={currentDays}
                  onChange={handleDaysChange}
                  className="w-full h-24 border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                >
                  {daysOfWeek.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Hora</label>
                <input
                  type="time"
                  value={currentTime}
                  onChange={e => setCurrentTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleAddSchedule}
              className="mt-3 px-4 py-2 bg-[#961914] text-white text-sm font-semibold rounded-lg hover:bg-red-800 transition"
            >
              + Agregar horario
            </button>
          </div>
        )}

        {/* Horarios agregados */}
        {teacherSubdisciplineSchedules.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-bold text-gray-700 mb-2">Horarios asignados</h4>
            <div className="space-y-2">
              {teacherSubdisciplineSchedules.map((sch, i) => (
                <div key={i} className="flex justify-between items-center bg-gray-50 border border-gray-200 px-4 py-2 rounded-lg">
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">{sch.subdiscipline}</span>
                    <span className="text-gray-400"> · {sch.level} · {sch.days.join(', ')} @ {sch.time}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSchedule(sch)}
                    className="text-red-400 hover:text-red-600 text-sm font-semibold"
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <SharedButton type="submit">
            {editingTeacher ? 'Guardar cambios' : 'Registrar maestro'}
          </SharedButton>
          {editingTeacher && (
            <SharedButton type="button" onClick={resetForm} className="bg-gray-500 hover:bg-gray-600">
              Cancelar
            </SharedButton>
          )}
        </div>
      </form>

      {/* ── Filtro de salario ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
        <h4 className="text-sm font-bold text-gray-700 mb-3">Calcular salario acumulado</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha inicio</label>
            <input type="date" value={salaryStart} onChange={e => setSalaryStart(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha fin</label>
            <input type="date" value={salaryEnd} onChange={e => setSalaryEnd(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
          </div>
        </div>
      </div>

      {/* ── Tabla de maestros ── */}
      {teachers.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">👩‍🏫</div>
          <p className="text-sm">No hay maestros registrados.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
                <th className="py-3 px-4 text-left">Nombre</th>
                <th className="py-3 px-4 text-left">Disciplinas</th>
                <th className="py-3 px-4 text-left">Horarios</th>
                <th className="py-3 px-4 text-right">Salario/Clase</th>
                <th className="py-3 px-4 text-right">Acumulado</th>
                <th className="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teachers.map(t => (
                <tr key={t.id} className={`hover:bg-gray-50 transition-colors ${editingTeacher?.id === t.id ? 'bg-[#961914]/5' : ''}`}>
                  <td className="py-3 px-4 font-semibold text-sm text-gray-900 whitespace-nowrap">{t.name}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{getDisciplineNames(t.disciplines)}</td>
                  <td className="py-3 px-4">
                    {Array.isArray(t.subdisciplineSchedules) && t.subdisciplineSchedules.length > 0 ? (
                      <div className="space-y-1">
                        {t.subdisciplineSchedules.map((s, i) => (
                          <div key={i} className="text-xs text-gray-600">
                            <span className="font-medium">{s.subdiscipline}</span>
                            <span className="text-gray-400"> · {s.level} · {s.days?.join(', ')} @ {s.time}</span>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-700">
                    {mxn(t.salaryPerClass || 0)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-[#961914]">
                    {mxn(calculateSalary(t))}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => handleEdit(t)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                        Editar
                      </button>
                      <button onClick={() => handleDelete(t.id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SharedCard>
  );
};

export default TeacherManagementForm;